import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { Brain, TrendingUp, Target, CheckCircle, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';

const BeliefsModule = () => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'diagnostic', 'dashboard', 'reinforcement'
  const [diagnosticCompleted, setDiagnosticCompleted] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load initial data
  useEffect(() => {
    const loadBeliefData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/beliefs/dashboard`);
        if (response.data.has_data) {
          setDiagnosticCompleted(true);
          setDashboardData(response.data);
        }
      } catch (error) {
        console.error('Error loading belief data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBeliefData();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-slate-600">{t('beliefs.ui.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {activeView === 'overview' && (
        <BeliefsOverview 
          diagnosticCompleted={diagnosticCompleted}
          dashboardData={dashboardData}
          onStartDiagnostic={() => setActiveView('diagnostic')}
          onViewDashboard={() => setActiveView('dashboard')}
          onStartReinforcement={() => setActiveView('reinforcement')}
        />
      )}

      {activeView === 'diagnostic' && (
        <BeliefsDiagnostic 
          onComplete={async (submissionData) => {
            setDiagnosticCompleted(true);
            // Fetch full dashboard data after submission
            try {
              const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/beliefs/dashboard`);
              if (response.data.has_data) {
                setDashboardData(response.data);
                setActiveView('dashboard');
              }
            } catch (error) {
              console.error('Error loading dashboard data:', error);
              // Fallback to submission data if dashboard fetch fails
              setDashboardData(submissionData);
              setActiveView('dashboard');
            }
          }}
          onBack={() => setActiveView('overview')}
        />
      )}

      {activeView === 'dashboard' && (
        <BeliefsDashboard 
          data={dashboardData}
          onBack={() => setActiveView('overview')}
          onStartReinforcement={() => setActiveView('reinforcement')}
        />
      )}

      {activeView === 'reinforcement' && (
        <BeliefsReinforcement 
          data={dashboardData}
          onBack={() => setActiveView('overview')}
        />
      )}
    </div>
  );
};

// Overview Component
const BeliefsOverview = ({ diagnosticCompleted, dashboardData, onStartDiagnostic, onViewDashboard, onStartReinforcement }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <Card className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white">
        <CardContent className="p-8">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Brain className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-2">{t('beliefs.title')}</h2>
              <p className="text-lg opacity-90">{t('beliefs.description')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Diagnostic Card */}
        <Card className="border-2 border-purple-200 hover:border-purple-400 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              {t('beliefs.diagnostic.title')}
            </CardTitle>
            <CardDescription>{t('beliefs.diagnostic.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {diagnosticCompleted ? (
              <div className="space-y-3">
                <Badge className="bg-green-100 text-green-800">{t('beliefs.ui.completed')}</Badge>
                <Button 
                  onClick={onStartDiagnostic}
                  variant="outline"
                  className="w-full"
                >
                  {t('beliefs.diagnostic.retake')}
                </Button>
              </div>
            ) : (
              <Button 
                onClick={onStartDiagnostic}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {t('beliefs.diagnostic.start')}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Dashboard Card */}
        <Card className="border-2 border-indigo-200 hover:border-indigo-400 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-600" />
              {t('beliefs.dashboard.title')}
            </CardTitle>
            <CardDescription>{t('beliefs.dashboard.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {diagnosticCompleted ? (
              <Button 
                onClick={onViewDashboard}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
              >
                {t('beliefs.results.viewDashboard')}
              </Button>
            ) : (
              <p className="text-sm text-slate-500">{t('beliefs.dashboard.completeDiagnostic')}</p>
            )}
          </CardContent>
        </Card>

        {/* Reinforcement Card */}
        <Card className="border-2 border-teal-200 hover:border-teal-400 transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-teal-600" />
              {t('beliefs.reinforcement.title')}
            </CardTitle>
            <CardDescription>{t('beliefs.reinforcement.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {diagnosticCompleted ? (
              <Button 
                onClick={onStartReinforcement}
                className="w-full bg-teal-600 hover:bg-teal-700"
              >
                {t('beliefs.results.startReinforcement')}
              </Button>
            ) : (
              <p className="text-sm text-slate-500">{t('beliefs.reinforcement.noData')}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Beliefs Diagnostic Component
const BeliefsDiagnostic = ({ onComplete, onBack }) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Define questions based on categories
  const questions = [
    // Money Beliefs (4 questions)
    { id: 'money_1', category: 'money', text: t('beliefs.diagnostic.questions.money_1') },
    { id: 'money_2', category: 'money', text: t('beliefs.diagnostic.questions.money_2') },
    { id: 'money_3', category: 'money', text: t('beliefs.diagnostic.questions.money_3') },
    { id: 'money_4', category: 'money', text: t('beliefs.diagnostic.questions.money_4') },
    // Wealth Beliefs (4 questions)
    { id: 'wealth_1', category: 'wealth', text: t('beliefs.diagnostic.questions.wealth_1') },
    { id: 'wealth_2', category: 'wealth', text: t('beliefs.diagnostic.questions.wealth_2') },
    { id: 'wealth_3', category: 'wealth', text: t('beliefs.diagnostic.questions.wealth_3') },
    { id: 'wealth_4', category: 'wealth', text: t('beliefs.diagnostic.questions.wealth_4') },
    // Success Beliefs (4 questions)
    { id: 'success_1', category: 'success', text: t('beliefs.diagnostic.questions.success_1') },
    { id: 'success_2', category: 'success', text: t('beliefs.diagnostic.questions.success_2') },
    { id: 'success_3', category: 'success', text: t('beliefs.diagnostic.questions.success_3') },
    { id: 'success_4', category: 'success', text: t('beliefs.diagnostic.questions.success_4') },
    // Abundance Beliefs (4 questions)
    { id: 'abundance_1', category: 'abundance', text: t('beliefs.diagnostic.questions.abundance_1') },
    { id: 'abundance_2', category: 'abundance', text: t('beliefs.diagnostic.questions.abundance_2') },
    { id: 'abundance_3', category: 'abundance', text: t('beliefs.diagnostic.questions.abundance_3') },
    { id: 'abundance_4', category: 'abundance', text: t('beliefs.diagnostic.questions.abundance_4') },
    // Self-Worth Beliefs (4 questions)
    { id: 'self_worth_1', category: 'self_worth', text: t('beliefs.diagnostic.questions.self_worth_1') },
    { id: 'self_worth_2', category: 'self_worth', text: t('beliefs.diagnostic.questions.self_worth_2') },
    { id: 'self_worth_3', category: 'self_worth', text: t('beliefs.diagnostic.questions.self_worth_3') },
    { id: 'self_worth_4', category: 'self_worth', text: t('beliefs.diagnostic.questions.self_worth_4') },
  ];

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  const handleAnswer = (rating) => {
    setResponses({ ...responses, [currentQ.id]: rating });
  };

  const handleNext = () => {
    if (responses[currentQ.id]) {
      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        handleSubmit();
      }
    } else {
      toast.error('Please select an answer');
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        responses: Object.entries(responses).map(([question_id, rating]) => ({
          question_id,
          rating: parseInt(rating)
        })),
        language: 'en' // TODO: Get from language context
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/beliefs/diagnostic/submit`,
        payload
      );

      toast.success('Diagnostic completed successfully!');
      onComplete(response.data);
    } catch (error) {
      console.error('Error submitting diagnostic:', error);
      toast.error('Failed to submit diagnostic. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-50 p-4">
      <div className="max-w-2xl mx-auto pt-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-2xl font-bold text-black mb-2">{t('beliefs.diagnostic.title')}</h1>
              <p className="text-slate-600">
                {t('beliefs.diagnostic.questionOf', { current: currentQuestion + 1, total: questions.length })}
              </p>
            </div>
            <Button variant="outline" onClick={onBack}>
              {t('beliefs.ui.exit')}
            </Button>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div 
              className="bg-gradient-to-r from-purple-500 to-indigo-600 h-4 rounded-full transition-all duration-500 flex items-center justify-end pr-2"
              style={{ width: `${progress}%` }}
            >
              <span className="text-white text-xs font-medium">{Math.round(progress)}%</span>
            </div>
          </div>
        </div>

        {/* Question Card */}
        <Card className="shadow-lg border-0 backdrop-blur-sm bg-white/90">
          <CardHeader>
            <Badge variant="outline" className="mb-4 w-fit">
              {t(`beliefs.diagnostic.categories.${currentQ.category}`)}
            </Badge>
            <CardTitle className="text-xl text-slate-800 leading-relaxed">
              {currentQ.text}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Likert Scale Options */}
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((rating) => (
                <label
                  key={rating}
                  className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all hover:bg-purple-50 ${
                    responses[currentQ.id] === rating
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-slate-200'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQ.id}`}
                    value={rating}
                    checked={responses[currentQ.id] === rating}
                    onChange={(e) => handleAnswer(parseInt(e.target.value))}
                    className="w-4 h-4 text-purple-600 mr-3"
                  />
                  <span className="text-slate-700">{t(`beliefs.diagnostic.scale.${rating}`)}</span>
                </label>
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-6">
              <Button
                onClick={handleBack}
                variant="outline"
                disabled={currentQuestion === 0}
              >
                {t('beliefs.ui.previous')}
              </Button>
              <Button
                onClick={handleNext}
                disabled={!responses[currentQ.id] || submitting}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {currentQuestion === questions.length - 1 
                  ? (submitting ? t('beliefs.diagnostic.submitting') : t('beliefs.diagnostic.submit'))
                  : t('beliefs.ui.next')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Beliefs Dashboard Component
const BeliefsDashboard = ({ data, onBack, onStartReinforcement }) => {
  const { t } = useTranslation();

  if (!data || !data.has_data) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-slate-600 mb-4">{t('beliefs.dashboard.noData')}</p>
          <Button onClick={onBack}>{t('beliefs.ui.goBack')}</Button>
        </CardContent>
      </Card>
    );
  }

  // Prepare bar chart data - bars go from 0 to score value
  const chartData = Object.entries(data.category_scores || {}).map(([category, score]) => {
    // All bars are dark purple
    const barColor = '#6b21a8'; // Dark purple
    
    return {
      category: t(`beliefs.diagnostic.categories.${category}`),
      score: score, // Use original 1-5 score directly
      originalScore: score,
      color: barColor
    };
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl">{t('beliefs.dashboard.title')}</CardTitle>
              <CardDescription>{t('beliefs.dashboard.description')}</CardDescription>
            </div>
            <Button variant="outline" onClick={onBack}>
              {t('beliefs.ui.goBack')}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Diverging Bar Chart */}
      <Card>
        <CardContent className="pt-6 pl-2 pr-4 md:pl-4 md:pr-8 lg:pl-6 lg:pr-12">
          {/* Legend - Responsive */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg">
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-6">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded flex-shrink-0" style={{ background: 'linear-gradient(to right, #b91c1c, #dc2626)' }}></div>
                <span className="text-slate-700 font-medium text-xs md:text-sm">{t('beliefs.ui.limitingLabel')} ({t('beliefs.ui.limitingRange')})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded flex-shrink-0" style={{ background: 'linear-gradient(to right, #f97316, #fbbf24)' }}></div>
                <span className="text-slate-700 font-medium text-xs md:text-sm">{t('beliefs.ui.neutralLabel')} ({t('beliefs.ui.neutralRange')})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded flex-shrink-0" style={{ background: 'linear-gradient(to right, #86efac, #16a34a)' }}></div>
                <span className="text-slate-700 font-medium text-xs md:text-sm">{t('beliefs.ui.growthLabel')} ({t('beliefs.ui.growthRange')})</span>
              </div>
            </div>
            <div className="mt-3 text-center">
              <span className="text-xs text-slate-600">Purple bars show your belief scores on a gradient background</span>
            </div>
          </div>

          {/* Chart Title */}
          <div className="flex items-center mb-4" style={{ paddingLeft: '140px', paddingRight: '30px' }}>
            <div className="flex-1 text-center">
              <h3 className="text-base md:text-xl font-bold text-slate-700">
                {t('beliefs.ui.chartAxisLeft')} | {t('beliefs.ui.chartAxisRight')}
              </h3>
            </div>
          </div>

          {/* Scale Labels Above Chart - Aligned with chart area */}
          <div className="flex items-center mb-2" style={{ paddingLeft: '140px' }}>
            <div className="flex justify-between items-center flex-1" style={{ paddingRight: '30px' }}>
              <span className="text-xs font-medium text-slate-600">{t('beliefs.ui.scaleVeryLow')}</span>
              <span className="text-xs font-medium text-slate-600">{t('beliefs.ui.scaleLow')}</span>
              <span className="text-xs font-medium text-slate-600">{t('beliefs.ui.scaleNeutral')}</span>
              <span className="text-xs font-medium text-slate-600">{t('beliefs.ui.scaleHigh')}</span>
              <span className="text-xs font-medium text-slate-600">{t('beliefs.ui.scaleVeryHigh')}</span>
            </div>
          </div>

          {/* Diverging Bar Chart - Responsive */}
          <div className="w-full overflow-x-auto">
            <div className="min-w-[320px]">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart 
                  data={chartData} 
                  layout="vertical"
                  margin={{ top: 5, right: 15, left: 15, bottom: 5 }}
                >
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#b91c1c" stopOpacity={0.4} />
                  <stop offset="30%" stopColor="#dc2626" stopOpacity={0.35} />
                  <stop offset="50%" stopColor="#fbbf24" stopOpacity={0.3} />
                  <stop offset="70%" stopColor="#86efac" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#16a34a" stopOpacity={0.4} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" fill="url(#colorGradient)" vertical={false} />
              <XAxis 
                type="number" 
                domain={[0, 5]}
                ticks={[1, 2, 3, 4, 5]}
                tick={{ fontSize: 10 }}
              />
              <YAxis 
                type="category" 
                dataKey="category" 
                width={110}
                tick={{ fontSize: 11 }}
              />
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-white p-3 border border-slate-200 rounded shadow-lg">
                        <p className="font-semibold text-slate-800">{data.category}</p>
                        <p className="text-sm text-slate-600">
                          {t('beliefs.ui.scoreLabel')}: {data.originalScore.toFixed(2)} / 5.0
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {data.originalScore <= 2.5 ? t('beliefs.ui.limitingBelief') : 
                           data.originalScore <= 3.5 ? t('beliefs.ui.neutralBelief') : t('beliefs.ui.growthBelief')}
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="score" barSize={25}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
            </div>
          </div>

          {/* Score Details */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            {chartData.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <span className="text-sm font-medium text-slate-700">{item.category}</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold" style={{ color: item.color }}>
                    {item.originalScore.toFixed(1)}
                  </span>
                  <span className="text-xs text-slate-500">/ 5.0</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Growth and Limiting Beliefs */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Growth Beliefs */}
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <CheckCircle className="w-5 h-5" />
              {t('beliefs.dashboard.growthBeliefs')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.growth_beliefs && data.growth_beliefs.length > 0 ? (
              <ul className="space-y-2">
                {data.growth_beliefs.map((belief, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-green-900">{t(`beliefs.diagnostic.categories.${belief}`)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-green-700">{t('beliefs.ui.noGrowthBeliefs')}</p>
            )}
          </CardContent>
        </Card>

        {/* Limiting Beliefs */}
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Target className="w-5 h-5" />
              {t('beliefs.dashboard.limitingBeliefs')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.limiting_beliefs && data.limiting_beliefs.length > 0 ? (
              <ul className="space-y-2">
                {data.limiting_beliefs.map((belief, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <Target className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
                    <span className="text-sm text-amber-900">{t(`beliefs.diagnostic.categories.${belief}`)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-amber-700">{t('beliefs.ui.noLimitingBeliefs')}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* CTA */}
      {data.limiting_beliefs && data.limiting_beliefs.length > 0 && (
        <Card className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white">
          <CardContent className="p-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('beliefs.ui.readyToTransform')}</h3>
            <p className="mb-4 opacity-90">{t('beliefs.ui.startExercises')}</p>
            <Button 
              onClick={onStartReinforcement}
              className="bg-white text-teal-600 hover:bg-gray-100"
            >
              {t('beliefs.results.startReinforcement')}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Beliefs Reinforcement Component
const BeliefsReinforcement = ({ data, onBack }) => {
  const { t } = useTranslation();
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!data || !data.has_data) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-slate-600 mb-4">{t('beliefs.reinforcement.noData')}</p>
          <Button onClick={onBack}>{t('beliefs.ui.goBack')}</Button>
        </CardContent>
      </Card>
    );
  }

  const handleCompleteExercise = async (category) => {
    if (!selectedExercise) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/beliefs/reinforcement/complete`,
        {
          belief_category: category,
          exercise_id: `${category}_exercise_1`,
          notes: notes || null
        }
      );

      toast.success(t('beliefs.ui.exerciseCompleted'));
      setSelectedExercise(null);
      setNotes('');
    } catch (error) {
      console.error('Error completing exercise:', error);
      toast.error('Failed to save exercise completion');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl">{t('beliefs.reinforcement.title')}</CardTitle>
              <CardDescription>{t('beliefs.reinforcement.description')}</CardDescription>
            </div>
            <Button variant="outline" onClick={onBack}>
              {t('beliefs.ui.goBack')}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Focus Areas */}
      {data.limiting_beliefs && data.limiting_beliefs.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-900">{t('beliefs.reinforcement.focusAreas')}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-amber-800 mb-4">
              These belief categories show the most room for growth. Focus your reinforcement exercises here:
            </p>
            <div className="flex flex-wrap gap-2">
              {data.limiting_beliefs.map((belief, idx) => (
                <Badge key={idx} className="bg-amber-200 text-amber-900">
                  {t(`beliefs.diagnostic.categories.${belief}`)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reinforcement Exercises */}
      <div className="grid md:grid-cols-2 gap-6">
        {data.limiting_beliefs && data.limiting_beliefs.map((category, idx) => (
          <Card key={idx} className="border-2 hover:border-teal-400 transition-colors">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-teal-600" />
                {t(`beliefs.reinforcement.categories.${category}`)}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-sm mb-1">{t(`beliefs.reinforcement.exercises.${category}.title`)}</h4>
                <p className="text-sm text-slate-600">{t(`beliefs.reinforcement.exercises.${category}.description`)}</p>
              </div>

              {selectedExercise === category ? (
                <div className="space-y-3">
                  <Textarea
                    placeholder={t('beliefs.reinforcement.notesPlaceholder')}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-[100px]"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleCompleteExercise(category)}
                      disabled={submitting}
                      className="flex-1 bg-teal-600 hover:bg-teal-700"
                    >
                      {submitting ? t('beliefs.ui.saving') : t('beliefs.reinforcement.markComplete')}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedExercise(null);
                        setNotes('');
                      }}
                    >
                      {t('beliefs.ui.cancel')}
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={() => setSelectedExercise(category)}
                  className="w-full"
                  variant="outline"
                >
                  {t('beliefs.reinforcement.completeExercise')}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default BeliefsModule;
